import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
} from "@/components/ui/form";
import { CustomerFormData } from "./types";
import { HTTPError } from 'ky';
import { api } from "../client/backend";

interface PromoCode {
  id: string;
  code: string;
  discount_percentage: number;
  valid_until?: string;
  max_uses?: number;
  current_uses: number;
  event_id: string;
}

interface PromoCodeSectionProps {
  form: UseFormReturn<CustomerFormData>;
  eventId: string;
  onPromoCodeApplied: (discountPercentage: number) => void;
}

export const PromoCodeSection = ({ form, eventId, onPromoCodeApplied }: PromoCodeSectionProps) => {
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [isCheckingPromo, setIsCheckingPromo] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const checkPromoCode = async (code: string) => {
    setIsCheckingPromo(true);
    setPromoError(null);

    try {
      // Convert the input code to uppercase for case-insensitive comparison
      const upperCode = code.toUpperCase();
      console.log("Checking promo code:", upperCode, "for event:", eventId);

      let promoCodeData: PromoCode | null = null;
      try {
        promoCodeData = await api.get('promo_codes', {
          searchParams: { code: upperCode, event_id: eventId }
        }).json<PromoCode>();
        console.log("Promo code check result:", promoCodeData);
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 404) {
          console.log("Promo code not found");
          setPromoError("Invalid promo code");
          setIsCheckingPromo(false);
          return;
        } else {
          console.error("Error checking promo code:", error);
          setPromoError("Error validating promo code");
          setIsCheckingPromo(false);
          return;
        }
      }

      if (!promoCodeData) {
        setPromoError("Invalid promo code");
        setIsCheckingPromo(false);
        return;
      }

      const now = new Date();
      if (promoCodeData.valid_until && new Date(promoCodeData.valid_until) < now) {
        setPromoError("Promo code has expired");
        setIsCheckingPromo(false);
        return;
      }

      if (promoCodeData.max_uses && promoCodeData.current_uses >= promoCodeData.max_uses) {
        setPromoError("Promo code has reached maximum uses");
        setIsCheckingPromo(false);
        return;
      }

      onPromoCodeApplied(promoCodeData.discount_percentage);

      // Update promo code usage in a separate operation
      try {
        await api.patch(`promo_codes/${promoCodeData.id}`, {
          json: { current_uses: promoCodeData.current_uses + 1 }
        });
        console.log("Successfully updated promo code usage");
      } catch (error) {
        console.error("Error updating promo code usage:", error);
        // Optionally handle this error, but don't block the user from proceeding
      }

    } catch (error) {
      console.error("Error checking promo code:", error);
      setPromoError("Error validating promo code");
    } finally {
      setIsCheckingPromo(false);
    }
  };

  if (!showPromoCode) {
    return (
      <button
        type="button"
        onClick={() => setShowPromoCode(true)}
        className="text-purple-500 hover:text-purple-600 font-medium"
      >
        Have a promo code?
      </button>
    );
  }

  return (
    <FormField
      control={form.control}
      name="promoCode"
      render={({ field }) => (
        <FormItem>
          <div className="flex gap-2">
            <Input
              placeholder="Enter promo code"
              {...field}
              className="bg-white text-black"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => checkPromoCode(field.value)}
              disabled={isCheckingPromo || !field.value}
            >
              Apply
            </Button>
          </div>
          {promoError && (
            <p className="text-sm text-red-500 mt-1">{promoError}</p>
          )}
        </FormItem>
      )}
    />
  );
};
